import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { playCue } from '../lib/foley';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const gameUrl = window.location.href;

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Reset copied state when modal closes
  useEffect(() => {
    if (!isOpen && copied) {
      setCopied(false);
    }
  }, [isOpen, copied]);

  if (!isOpen) {
    return null;
  }

  const handleOverlayClick = () => {
    onClose();
  };

  const handleModalClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(gameUrl);
      playCue('success');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      playCue('error');
      console.error('Failed to copy:', err);
    }
  };

  const handleUrlClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.currentTarget.select();
  };

  return (
    <div className="modal-overlay" role="presentation" onClick={handleOverlayClick}>
      <div className="modal" onClick={handleModalClick}>
        <div className="modal__header">
          <h2>Invite Others</h2>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close invite modal"
            data-foley-click="drop"
          >
            ×
          </button>
        </div>
        <div className="modal__content">
          <p className="modal__helper">Share this link with your team to join the planning session.</p>
          
          <div className="invite-modal__url-group">
            <label htmlFor="invite-url" className="modal__label">
              Game URL
            </label>
            <input
              id="invite-url"
              type="text"
              value={gameUrl}
              readOnly
              onClick={handleUrlClick}
              className="modal__input invite-modal__url-input"
              aria-label="Game invite URL"
            />
          </div>
          
          <button
            type="button"
            className="modal__button invite-modal__copy-button"
            onClick={handleCopyClick}
          >
            {copied ? '✓ Copied!' : 'Copy invite link'}
          </button>
          
          {copied && (
            <p className="invite-modal__copied" role="status" aria-live="polite">
              Link copied to clipboard
            </p>
          )}
          
          <div className="invite-modal__qr-container">
            <p className="modal__label">Or scan QR code</p>
            <QRCodeSVG
              value={gameUrl}
              size={200}
              level="M"
              includeMargin={true}
              className="invite-modal__qr-code"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteModal;
